/**
 * vueCronEditorMixin
 * Core logic of a component.
 * Functionality dependent on UI frameworks should be implemented in derived components
 */

import {
  buildExpression,
  parseExpression,
  TabUpdatedEvent,
  TabKey
} from './cronExpressions'

import Vue from 'vue'

const initialData = {
  date: {
    type: 'date',
    minutes: 0,
    hours: 0,
    day: 0,
    month: 0,
    year: 1970
  },
  hourly: {
    type: 'hourly',
    minutes: 0,
    hourInterval: 1
  },
  daily: {
    type: 'daily',
    minutes: 0,
    hours: 0,
    dayInterval: 1
  },
  weekly: {
    type: 'weekly',
    minutes: 0,
    hours: 0,
    days: ['1']
  },
  monthly: {
    type: 'monthly',
    hours: 0,
    minutes: 0,
    days: ['1'],
    monthInterval: 1
  },
  custom: {
    type: 'custom',
    cronExpression: ''
  }
}

interface ComponentData {
  editorData: Object
  innerValue: string | null
  description: string | null
  currentTab: TabKey
}

export default Vue.extend({
  created() {
    this.innerValue = this.value
    this.__loadDataFromExpression()
  },
  props: {
    value: { type: String, default: '*/1 * * * *' },
    preserveStateOnSwitchToCustom: { type: Boolean, default: false }
  },
  data() {
    return <ComponentData>{
      innerValue: '*/1 * * * *',
      description: '',
      editorData: Object.assign(
        {},
        (): TabUpdatedEvent => {
          let currentDate = new Date()
          return {
            type: 'date',
            minutes: currentDate.getMinutes(),
            hours: currentDate.getHours(),
            day: currentDate.getDate(),
            month: currentDate.getMonth() + 1,
            year: currentDate.getFullYear()
          }
        }
      ),
      currentTab: 'date',
      nextEvents: [],
      i18n: null
    }
  },
  computed: {
    explanation() {
      const weekday = {
        1: 'Sunday',
        2: 'Monday',
        3: 'Tuesday',
        4: 'Wednesday',
        5: 'Thursday',
        6: 'Friday',
        7: 'Saturday'
      }

      let current = this.$data.editorData
      let fullMinutes =
        current.minutes < 10 ? `0${current.minutes}` : current.minutes
      let fullHours = current.hours < 10 ? `0${current.hours}` : current.hours

      if (current.type === 'custom') {
        return `Custom Cron Expression`
      }
      if (current.type === 'date') {
        return `Run on ${current.day}.${current.month}.${current.year} at ${fullHours}:${fullMinutes} UTC`
      }
      if (current.type === 'hourly') {
        return `Run every ${current.hourInterval} hour(s) at ${fullMinutes} UTC`
      }
      if (current.type === 'daily') {
        return `Run every ${current.dayInterval} day(s) at ${fullHours}:${fullMinutes} UTC`
      }
      if (current.type === 'weekly') {
        return `Run every ${current.days
          .map(pos => weekday[pos])
          .join(',')} at ${fullHours}:${fullMinutes} UTC`
      }
      if (current.type === 'monthly') {
        return `Run every ${
          current.monthInterval
        } month(s) on day(s) ${current.days
          .map(day => (day === 'L' ? 'Last' : day))
          .join(',')} at ${fullHours}:${fullMinutes} UTC`
      }
      return ''
    }
  },
  methods: {
    __loadDataFromExpression() {
      const tabData = parseExpression(this.value)
      this.$data.editorData = { ...tabData }
      this.currentTab = tabData.type
    },
    __updateCronExpression(event: TabUpdatedEvent) {
      const cronExpression = buildExpression({
        ...event
      })
      this.innerValue = cronExpression
      this.$emit('input', cronExpression)
    }
  },
  watch: {
    value: {
      handler() {
        if (this.value == this.innerValue) {
          return
        }
        this.__loadDataFromExpression()
      }
    },
    editorData: {
      deep: true,
      handler(changedData) {
        this.__updateCronExpression(changedData)
      }
    }
  }
})
